import React, { useState } from 'react';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  Typography, 
  List, 
  ListItem,
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  IconButton, 
  Divider, 
  Avatar, 
  useTheme,
  useMediaQuery,
  Tooltip,
  Menu,
  MenuItem,
  Badge,
  Container,
  Collapse
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard as DashboardIcon, 
  CalendarToday as CalendarIcon, 
  DirectionsBoat as BoatIcon, 
  EventNote as ReservationsIcon, 
  Person as PersonIcon, 
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckInIcon,
  ClearAll as ClearAllIcon,
  Anchor as AnchorIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { setLoggedOutState } from '../utils/auth';
import { getUserInitials } from '../utils/userUtils';

const drawerWidth = 240;
const collapsedDrawerWidth = 72;

interface LaunchRampLayoutProps {
  children: React.ReactNode;
}

const LaunchRampLayout: React.FC<LaunchRampLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { userProfile, signOut } = useAuth();
  const { unreadReservations, clearReservationNotifications } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [reservationsOpen, setReservationsOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  // Menu for user account
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Notification menu
  const handleNotificationMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleClearNotifications = () => {
    clearReservationNotifications();
    handleNotificationClose();
  };

  const handleViewReservations = () => {
    navigate('/staff/reservations');
    handleNotificationClose();
  };

  const handleLogout = async () => {
    setLoggedOutState(); // Immediately flag as logged out
    await signOut();
    window.location.href = '/auth?logout=true';
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleReservationsClick = () => {
    setReservationsOpen(!reservationsOpen);
  };

  const handleItemClick = (item: any) => {
    if (item.subItems) {
      setExpandedItem(expandedItem === item.text ? null : item.text);
    } else {
      navigate(item.path);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const handleSubItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerExpand = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Navigation items
  const navItems = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/staff/dashboard',
      active: location.pathname === '/staff/dashboard'
    },
    { 
      text: 'Reservations', 
      icon: <ReservationsIcon />, 
      path: '/staff/reservations',
      active: location.pathname.includes('/staff/reservations'),
      subItems: [
        {
          text: 'New Reservation',
          path: '/staff/new',
          active: location.pathname === '/staff/new'
        },
        {
          text: 'Check-In',
          path: '/staff/checkin',
          active: location.pathname === '/staff/checkin'
        }
      ]
    },
    { 
      text: 'Launch Ramp', 
      icon: <BoatIcon />, 
      path: '/staff/launch',
      active: location.pathname === '/staff/launch'
    },
    { 
      text: 'Slips', 
      icon: <AnchorIcon />, 
      path: '/staff/slips',
      active: location.pathname === '/staff/slips'
    }
  ];

  const drawer = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      width: drawerOpen ? 240 : 64,
      transition: 'width 0.2s ease-in-out'
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: drawerOpen ? 'space-between' : 'center',
        p: 2,
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        {drawerOpen && (
          <Typography variant="h6" noWrap component="div">
            Staff Portal
          </Typography>
        )}
        <IconButton onClick={handleDrawerExpand}>
          {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem 
              disablePadding 
              sx={{ 
                display: 'block',
                mb: 0.5
              }}
            >
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  minHeight: 48,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2.5,
                  bgcolor: item.active ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  '&:hover': {
                    bgcolor: item.active ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: item.active ? 'primary.main' : 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {drawerOpen && (
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{
                      color: item.active ? 'primary' : 'inherit',
                      fontWeight: item.active ? 600 : 400
                    }}
                  />
                )}
                {drawerOpen && item.subItems && (
                  <Box sx={{ ml: 'auto' }}>
                    {expandedItem === item.text ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                )}
              </ListItemButton>
            </ListItem>
            {drawerOpen && item.subItems && expandedItem === item.text && (
              <List component="div" disablePadding>
                {item.subItems.map((subItem) => (
                  <ListItem key={subItem.text} disablePadding>
                    <ListItemButton
                      component={Link}
                      to={subItem.path}
                      sx={{
                        pl: 4,
                        bgcolor: subItem.active ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        '&:hover': {
                          bgcolor: subItem.active ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <ListItemText 
                        primary={subItem.text}
                        primaryTypographyProps={{
                          color: subItem.active ? 'primary' : 'inherit',
                          fontWeight: subItem.active ? 600 : 400
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 48,
              justifyContent: drawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: drawerOpen ? 2 : 'auto',
                justifyContent: 'center',
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {drawerOpen && <ListItemText primary="Logout" />}
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerOpen ? 240 : 64}px)` },
          ml: { sm: `${drawerOpen ? 240 : 64}px` },
          transition: 'margin 0.2s ease-in-out, width 0.2s ease-in-out'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Fallen Leaf Marina - Launch Ramp
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Notifications">
              <IconButton 
                color="inherit" 
                onClick={handleNotificationMenu}
                aria-controls="notification-menu"
                aria-haspopup="true"
              >
                <Badge badgeContent={unreadReservations} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <Menu
              id="notification-menu"
              anchorEl={notificationAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(notificationAnchorEl)}
              onClose={handleNotificationClose}
            >
              {unreadReservations > 0 ? (
                <>
                  <MenuItem onClick={handleViewReservations}>
                    <ListItemIcon>
                      <ReservationsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {`${unreadReservations} new reservation${unreadReservations > 1 ? 's' : ''}`}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleClearNotifications}>
                    <ListItemIcon>
                      <ClearAllIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Clear notifications</ListItemText>
                  </MenuItem>
                </>
              ) : (
                <MenuItem>
                  <ListItemText>No new notifications</ListItemText>
                </MenuItem>
              )}
            </Menu>
            
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar 
                alt={`${userProfile?.firstName || 'Staff'} ${userProfile?.lastName || ''}`} 
                sx={{ width: 32, height: 32 }}
              >
                {getUserInitials(userProfile?.firstName, userProfile?.lastName)}
              </Avatar>
            </IconButton>
            
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerOpen ? 240 : 64 }, flexShrink: { sm: 0 }, transition: 'width 0.2s ease-in-out' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 240,
              borderRight: '1px solid rgba(0, 0, 0, 0.12)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerOpen ? 240 : 64,
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              transition: 'width 0.2s ease-in-out'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerOpen ? 240 : 64}px)` },
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          transition: 'width 0.2s ease-in-out'
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default LaunchRampLayout; 